import { Stack, Container, Button, Box } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import type { ReactNode } from "react";

interface OnboardingPageContainerProps {
  children: ReactNode;
  maxWidth?: number;
  onExit?: () => void;
}

/**
 * Consistent container for onboarding pages with exit button
 * Provides max width constraint and exit functionality
 */
const OnboardingPageContainer = ({
  children,
  maxWidth = 1600,
  onExit,
}: OnboardingPageContainerProps) => {
  const [canClose, setCanClose] = useState(false);

  useEffect(() => {
    // Only show exit button if:
    // 1. Window was opened by script (has opener) - can be safely closed
    // 2. Or has navigation history (user navigated here, not the first page)
    // 3. Or a custom onExit handler is provided
    const hasOpener = window.opener !== null;
    const hasHistory = window.history.length > 1;
    const hasCustomExit = !!onExit;

    // Debug logs with styling for visibility
    console.group("🔍 EXIT BUTTON DEBUG - OnboardingPageContainer");
    console.log("%c Window Information:", "font-weight: bold; color: blue;");
    console.log("  - window.opener:", window.opener);
    console.log("  - hasOpener:", hasOpener);
    console.log("%c Navigation History:", "font-weight: bold; color: blue;");
    console.log("  - window.history.length:", window.history.length);
    console.log("  - hasHistory:", hasHistory);
    console.log("%c Custom Exit Handler:", "font-weight: bold; color: blue;");
    console.log("  - onExit provided:", hasCustomExit);
    console.log(
      "%c Final Decision:",
      "font-weight: bold; color: " +
        (hasOpener || hasHistory || hasCustomExit ? "green" : "red") +
        ";"
    );
    console.log(
      "  - canClose (will show button):",
      hasOpener || hasHistory || hasCustomExit
    );
    console.log("%c Current URL:", "font-weight: bold; color: blue;");
    console.log("  - location.href:", window.location.href);
    console.log("  - location.pathname:", window.location.pathname);
    console.groupEnd();

    // Can close if window was opened by script, has navigation history, or has custom exit handler
    setCanClose(hasOpener || hasHistory || hasCustomExit);
  }, [onExit]);

  const handleExit = () => {
    // If custom exit handler is provided, use it
    if (onExit) {
      onExit();
      return;
    }

    // Otherwise try to go back first if there's history
    if (window.history.length > 1) {
      window.history.back();
    } else {
      // Otherwise attempt to close the tab
      window.close();
    }
  };

  return (
    <Container size={maxWidth} w="100%">
      <Stack gap="md" pb="xl">
        {children}

        {/* Exit button at bottom - only show if tab can be closed */}
        {canClose && (
          <Box mt="xl" style={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconX size={16} />}
              onClick={handleExit}
            >
              Exit and Close Tab
            </Button>
          </Box>
        )}
      </Stack>
    </Container>
  );
};

export default OnboardingPageContainer;
